'use client';
// TODO: Fix React 19 compiler lint errors. See https://react.dev/learn/react-compiler
/* eslint-disable react-hooks/set-state-in-effect */

import { GridColFullWidth, ScreenHorizontalPadding } from '@constants/layout';
import { REFERRAL_LP_KEY } from '@constants/referral';
import { MotionDiv } from '@lib/motion';
import { MenuSectionType, MenuType } from '@lib/sanity/queries/menu/types';
import { ThemeProjection } from '@lib/sanity/types/common';
import Anchor from '@ui/Anchor/Anchor';
import Container from '@ui/Container/Container';
import EmbedButton from '@ui/EmbedButton/EmbedButton';
import IconType from '@ui/Icon/StaticIcon/IconType';
import { StaticIcon } from '@ui/Icon/StaticIcon/StaticIcon';
import cn from '@utils/cn';
import { getUrlWithTrailingSlash } from '@utils/urls';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import AnchorButton from '../AnchorButton/AnchorButton';
import HeaderInternationalization from '../Internationalization/HeaderInternationalization';
import Link from './MenuSection/MenuCategory/Link';

type MenuBarProps = {
  isBurgerClicked?: boolean;
  setIsBurgerClicked?: (value: boolean) => void;
  setHoveredSection?: (value?: MenuSectionType | null) => void;
  selectedSection?: MenuSectionType | null;
  data: MenuType;
  theme?: ThemeProjection | null;
  hideBurger?: boolean;
  renderBrand?: React.ReactNode;
};

export const MenuBarHeight = 80;

export default function MenuBar({
  data,
  theme,
  setHoveredSection,
  selectedSection,
  isBurgerClicked,
  setIsBurgerClicked,
  hideBurger,
  renderBrand,
}: MenuBarProps) {
  const [primaryButtonUrl, setPrimaryButtonUrl] = useState(data?.buttonPrimary?.url ?? '');
  const pathname = usePathname();
  const isBlogRoute = typeof pathname === 'string' && pathname.includes('/blog');
  const logoHref = isBlogRoute
    ? getUrlWithTrailingSlash(data.menuLogo?.url ?? '/blog')
    : getUrlWithTrailingSlash(data.menuLogo?.url ?? '/');
  const logoAlt: string = (typeof data.menuLogo?.logo?.alt === 'string' && data.menuLogo?.logo?.alt) || 'Logo';

  function renderLogoContent() {
    if (data.menuLogo?.logo?.iconSvg?.result) {
      return (
        <div
          className="flex h-full "
          dangerouslySetInnerHTML={{
            __html: data.menuLogo?.logo?.iconSvg?.result,
          }}
        />
      );
    }

    const logoUrl = data.menuLogo?.logo?.icon?.asset?.url;
    const fallbackBlogLogoUrl = isBlogRoute && '/loop-logo.svg';
    const resolvedLogoSrc = logoUrl ?? fallbackBlogLogoUrl;

    if (resolvedLogoSrc) {
      return (
        <Image src={resolvedLogoSrc} className="h-full min-w-[119px]" alt={logoAlt} width={119} height={31} priority />
      );
    }
    return (
      <StaticIcon
        icon={IconType.theBriefLogo}
        className="fill-foreground h-auto w-auto cursor-pointer md:h-auto md:w-auto"
      />
    );
  }

  useEffect(() => {
    if (!data?.buttonPrimary?.url) {
      return;
    }

    if (!data?.isReferralLP) {
      setPrimaryButtonUrl(data.buttonPrimary.url);
      return;
    }

    const url = new URL(data.buttonPrimary.url, window.location.origin);
    url.searchParams.set(REFERRAL_LP_KEY, window.location.href);
    setPrimaryButtonUrl(url.toString());
  }, [data]);

  function onSectionMouseEnter(item: MenuSectionType) {
    if (setHoveredSection) {
      setHoveredSection(item);
    }
  }

  function onSectionMouseLeave() {
    if (setHoveredSection) {
      setHoveredSection();
    }
  }

  function onClickMenuItem(item: MenuSectionType) {
    if (item.menuCategories?.length && setHoveredSection) {
      if (selectedSection) {
        setHoveredSection();
      } else {
        setHoveredSection(item);
      }
    }
  }

  function onClickBurgerMenu(bool: boolean) {
    if (setIsBurgerClicked) {
      setIsBurgerClicked(bool);
    }
  }

  function showIcon() {
    if (hideBurger) {
      return;
    }
    if (isBurgerClicked) {
      return (
        <MotionDiv
          className={cn('flex', {
            'md:hidden!': isBlogRoute,
            'lg:hidden!': !isBlogRoute,
          })}
          animate={{ rotate: 180, transition: { duration: 0.15 } }}
        >
          <StaticIcon
            icon={IconType.close}
            className="fill-foreground w-auto cursor-pointer"
            onClick={() => onClickBurgerMenu(false)}
          />
        </MotionDiv>
      );
    }

    return (
      <MotionDiv
        className={cn('flex', {
          'md:hidden!': isBlogRoute,
          'lg:hidden!': !isBlogRoute,
        })}
        animate={{ rotate: 360, transition: { duration: 0.15 } }}
      >
        <StaticIcon
          icon={IconType.burgerMenu}
          className="fill-foreground w-auto cursor-pointer"
          onClick={() => onClickBurgerMenu(true)}
        />
      </MotionDiv>
    );
  }
  if (!(data?.theme && theme)) {
    return;
  }
  return (
    <Container
      className={cn('h-[80px] border-b', ScreenHorizontalPadding, {
        'bg-transparent': data.isBackgroundTransparent,
        'bg-background': !data.isBackgroundTransparent,
      })}
      style={{ transition: 'background-color 0.1s ease-in-out' }}
      theme={theme}
      paddingType="none"
    >
      <div className={cn('z-50 flex gap-6 xl:gap-7 w-full items-center h-full', GridColFullWidth)}>
        <div className="align-left h-full content-center justify-start">
          {renderBrand ? (
            renderBrand
          ) : (
            <Anchor href={logoHref} className="flex w-fit items-center">
              {renderLogoContent()}
            </Anchor>
          )}
        </div>
        <div className="align-right flex grow items-center h-full gap-2 sm:gap-7">
          <div
            className={cn('align-right flex h-full w-full select-none items-center justify-end lg:gap-3 xl:gap-7', {
              'lg:justify-between': data.justifyBetween,
            })}
          >
            <div
              className={cn('h-full', {
                'hidden md:flex': isBlogRoute,
                'hidden lg:flex': !isBlogRoute,
              })}
            >
              {data.menuSections?.map((section, sectionIndex) => {
                if (!section?.title?.value) {
                  return;
                }
                return (
                  <div className="flex" key={`${section.title.value}_${sectionIndex}`}>
                    {section.insertDividerBefore && <div className="bg-border mr-2 mt-5 h-[47px] w-px xl:mr-4"></div>}
                    <div
                      className="h-full"
                      key={sectionIndex}
                      onMouseEnter={() => onSectionMouseEnter(section)}
                      onMouseLeave={onSectionMouseLeave}
                    >
                      <div
                        onClick={() => onClickMenuItem(section)}
                        className={cn('h-full', {
                          'pr-2 xl:pr-4': !isBlogRoute,
                          'mr-6 xl:mr-7': isBlogRoute,
                        })}
                      >
                        <Link
                          className="h-full"
                          isActive={
                            selectedSection?.title?.value === section.title?.value &&
                            !!selectedSection.menuCategories?.length
                          }
                          menuCategories={section.menuCategories}
                          url={getUrlWithTrailingSlash(section.url)}
                          title={section.title}
                          useArrowDownIcon={isBlogRoute}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center gap-3 xl:gap-7">
              {data.addLanguageIcon && (
                <>
                  <HeaderInternationalization />
                  <div
                    className={cn('bg-border hidden h-[47px] w-px', {
                      'md:block': isBlogRoute,
                      'lg:block': !isBlogRoute,
                    })}
                  ></div>
                </>
              )}
              <AnchorButton
                button={
                  data.buttonLink
                    ? {
                        ...data.buttonLink,
                        size: 's',
                        variant: 'link',
                      }
                    : undefined
                }
                buttonClassName={cn(
                  'text-xs-medium text-foreground font-medium hover:no-underline p-0',
                  'text-foreground hidden hover:cursor-pointer',
                  {
                    'md:flex': isBlogRoute,
                    'lg:flex': !isBlogRoute,
                  },
                )}
              />
              <AnchorButton
                button={
                  data.buttonSecondary
                    ? {
                        ...data.buttonSecondary,
                        size: 's',
                        variant: 'outline',
                      }
                    : undefined
                }
                buttonClassName={cn('hidden h-[36px]', {
                  'md:flex': isBlogRoute,
                  'lg:flex': !isBlogRoute,
                })}
              />
              <AnchorButton
                button={
                  data.buttonPrimary
                    ? {
                        ...data.buttonPrimary,
                        size: 's',
                        variant: 'primary',
                        url: getUrlWithTrailingSlash(primaryButtonUrl),
                      }
                    : undefined
                }
                buttonClassName={cn('px-[12px] py-[4px] sm:px-[16px] sm:py-[6px] h-[36px]', {
                  'hidden md:flex': isBlogRoute,
                  'hidden sm:flex': !isBlogRoute,
                  'border-border border': isBlogRoute,
                })}
              />
              <AnchorButton
                button={
                  data.buttonPrimarySmallScreen
                    ? {
                        ...data.buttonPrimarySmallScreen,
                        size: 's',
                        variant: 'primary',
                        url: getUrlWithTrailingSlash(primaryButtonUrl),
                      }
                    : undefined
                }
                buttonClassName={cn('px-[12px] py-[4px] sm:px-[16px] sm:py-[6px] h-[36px]', {
                  'flex sm:hidden': !isBlogRoute,
                  hidden: isBlogRoute,
                  'border-border border': isBlogRoute,
                })}
              />
              {data.embedButton && <EmbedButton embedButton={data.embedButton} />}
            </div>
          </div>
          {showIcon()}
        </div>
      </div>
    </Container>
  );
}
